import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tabs } from 'antd';
import { ZipcodeColumns } from './components/ZipcodeColumns';
import ZipcodeFilter from './components/ZipcodeFilter';
import GeofenceListTab from './components/GeofenceListTab';
import ZipcodeMapDrawer from './components/ZipcodeMapDrawer';
import { ZipcodeFiltersType, ZipcodeType } from "./graphql/queries/getZipcodeListing";
import { useZipcodeQuery } from "./hooks/useZipcodeQuery";
import TablePaginationBar from '../../../components/TablePaginationBar';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';

const ZipcodeInitialValue: ZipcodeFiltersType = {};

const ZipcodeListTab: React.FC = () => {
    const { currentWarehouse } = useWarehouseContext();
    const [filters, setFilters] = useState<ZipcodeFiltersType>(ZipcodeInitialValue);
    const [tempFilters, setTempFilters] = useState<ZipcodeFiltersType>(ZipcodeInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [drawerZipcode, setDrawerZipcode] = useState<string | null>(null);
    const { t } = useTranslation();

    const handleGeofenceBlacklist = useCallback((record: ZipcodeType) => {
        setDrawerZipcode(record.zipcode);
    }, []);

    // 合并 warehouseId 到 filters
    const filtersWithWarehouse = {
        ...filters,
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined
    };

    const {
        zipcodeData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useZipcodeQuery(filtersWithWarehouse, currentPage, pageSize);

    const handleResetFilters = () => {
        setTempFilters(ZipcodeInitialValue);
        setFilters(ZipcodeInitialValue);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 当 warehouseId 变化时，重置到第一页并重新查询
    useEffect(() => {
        if (currentWarehouse?.id) {
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id]);

    // 添加useEffect监听状态变化后执行查询
    useEffect(() => {
        if (currentWarehouse?.id) {
            refetch(buildQueryVariables());
        }
    }, [filtersWithWarehouse.warehouseId, filters, currentPage, pageSize]);

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    return (
        <div>
            {/* 筛选条件 */}
            <ZipcodeFilter
                filters={tempFilters}
                setFilters={setTempFilters}
            />

            <div className="mb-2 flex justify-end gap-2">
                <Button
                    type="primary"
                    onClick={handleFilter}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.zipcodes.filters.filter')}</span>
                </Button>
                <Button
                    onClick={handleResetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.zipcodes.filters.reset')}</span>
                </Button>
            </div>

            {/* 邮编列表 */}
            <ZipcodeColumns
                zipcodeData={zipcodeData}
                loading={loading}
                onGeofenceBlacklist={handleGeofenceBlacklist}
            />

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                paginationProps={{ disabled: loading }}
            />

            {/* 围栏黑名单详情抽屉 */}
            <ZipcodeMapDrawer
                zipcode={drawerZipcode}
                onClose={() => setDrawerZipcode(null)}
            />
        </div>
    );
};


export const ZipcodesPage = () => {
    const { t } = useTranslation();
    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <Tabs
                defaultActiveKey="zipcode"
                items={[
                    {
                        key: 'zipcode',
                        label: t('wms.zipcodeMapGeofence.tabs.zipcode'),
                        children: <ZipcodeListTab />,
                    },
                    {
                        key: 'geofence',
                        label: t('wms.zipcodeMapGeofence.tabs.geofence'),
                        children: <GeofenceListTab />,
                    },
                ]}
            />
        </div>
    );
};